import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { FileSpreadsheet, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { getCurrentResultsByTbId } from '../services/uiShim';

const sumAmount = (rows) => (rows || []).reduce((acc, r) => acc + Number(r.CLAIMED_AMOUNT ?? r.GL_BALANCE ?? 0), 0);

const ICESchedules = ({ calculationId, filename, calculation, trialBalance }) => {
  const [schedules, setSchedules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tenantId = (trialBalance && trialBalance.tenant_id) || calculation?.tenant_id || '';
  const tbId = calculation?.tb_id || trialBalance?.id;

  useEffect(() => {
    if (tenantId && tbId) {
      loadSchedules();
    }
  }, [tenantId, tbId, calculation?.id, calculation?.recalculated_at]);

  useEffect(() => {
    // Post-adaptation fallback: if D rows present but all zeros, derive from uploaded TB
    if (!schedules || !trialBalance || !Array.isArray(trialBalance.accounts)) return;
    const d = schedules.D || {};
    const rows = d.rows || [];
    const total = sumAmount(rows);
    if (rows.length > 0 && total === 0) {
      const interAcc = trialBalance.accounts.filter(a => String(a.pool || '').toLowerCase().includes('intermediate'));
      if (interAcc.length > 0) {
        const tbRows = interAcc.map(a => ({
          ACCOUNT_NUMBER: a.account_number || a.account,
          ACCOUNT_NAME: a.account_name,
          GL_BALANCE: Number(a.net_amount || 0),
          ADJUSTMENTS: 0,
          CLAIMED_AMOUNT: Number(a.net_amount || 0),
        }));
        const tbTotal = tbRows.reduce((acc, r) => acc + (Number(r.CLAIMED_AMOUNT) || 0), 0);
        const newD = {
          ...d,
          rows: tbRows,
          summary: {
            ...(d.summary || {}),
            intermediate_total: d.summary?.intermediate_total || tbTotal,
            to_ga_amount: d.summary?.to_ga_amount || 0,
            to_oh_amount: d.summary?.to_oh_amount || 0,
          }
        };
        setSchedules(prev => ({ ...(prev || {}), D: newD }));
      }
    }
  }, [schedules, trialBalance]);

  const loadSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getCurrentResultsByTbId(tenantId, tbId);
      setSchedules(d?.schedules || null);
    } catch (error) {
      setError('Failed to load ICE schedules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => (value || value === 0 ? `$${Number(value).toLocaleString()}` : 'N/A');

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileSpreadsheet className="h-5 w-5" />
            <span>ICE Schedules A–E</span>
            <Badge variant="secondary">{filename}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Generating ICE Schedules...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileSpreadsheet className="h-5 w-5" />
            <span>ICE Schedules A–E</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-red-700">
              {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!schedules) return null;

  const schedA = schedules?.A || { title: 'Summary of Claimed Indirect Rates', rows: [] };
  const schedB = schedules?.B || { title: 'G&A Expenses', rows: [] };
  const schedC = schedules?.C || { title: 'Overhead Expenses', rows: [] };
  const schedD = schedules?.D || { title: 'Intermediate Allocations', summary: {}, rows: [] };
  const schedE = schedules?.E || { title: 'Allocation Bases', rows: [] };

  const renderBCHeader = () => (
    <TableHeader>
      <TableRow>
        <TableHead>Account #</TableHead>
        <TableHead>Account Name</TableHead>
        <TableHead className="text-right">GL Balance</TableHead>
        <TableHead className="text-right">Adjustments</TableHead>
        <TableHead className="text-right">Claimed</TableHead>
      </TableRow>
    </TableHeader>
  );

  const renderBCRows = (rows) => (
    <TableBody>
      {rows.map((r, idx) => {
        const acct = String(r.ACCOUNT_NUMBER || '').toUpperCase();
        const name = String(r.ACCOUNT_NAME || '').toUpperCase();
        const isAdj = acct.startsWith('ADJ');
        const isTotal = acct === 'TOTAL' || name.includes('TOTAL');
        const rowClass = isTotal ? 'font-bold' : (isAdj ? 'text-blue-700 italic' : '');
        return (
          <TableRow key={idx} className={rowClass}>
            <TableCell>{r.ACCOUNT_NUMBER || ''}</TableCell>
            <TableCell>{r.ACCOUNT_NAME || ''}</TableCell>
            <TableCell className="text-right">{formatCurrency(r.GL_BALANCE ?? 0)}</TableCell>
            <TableCell className="text-right">{formatCurrency(r.ADJUSTMENTS ?? 0)}</TableCell>
            <TableCell className="text-right">{formatCurrency(r.CLAIMED_AMOUNT ?? 0)}</TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );

  const renderScheduleA = () => (
    <div className="text-sm">
      <div className="font-semibold mb-2">{schedA.title}</div>
      {Array.isArray(schedA.rows) && schedA.rows.length > 0 ? (
        <Table className="w-full text-sm">
          <TableHeader>
            <TableRow>
              <TableHead>Pool</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedA.rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.pool}</TableCell>
                <TableCell className="text-right">{formatCurrency(r.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-gray-600">No summary data available.</div>
      )}
    </div>
  );

  const renderScheduleE = () => (
    <div className="text-sm">
      <div className="font-semibold mb-2">{schedE.title}</div>
      {Array.isArray(schedE.rows) && schedE.rows.length > 0 ? (
        <Table className="w-full text-sm">
          <TableHeader>
            <TableRow>
              <TableHead>Base</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedE.rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.base}</TableCell>
                <TableCell className="text-right">{formatCurrency(r.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-gray-600">No base data available.</div>
      )}
    </div>
  );

  const renderScheduleD = () => (
    <div className="text-sm space-y-3">
      {Array.isArray(schedD.rows) && schedD.rows.length > 0 && (
        <div className="mb-4">
          <div className="font-semibold mb-2">Intermediate Allocation Accounts</div>
          <Table className="w-full text-sm">
            {renderBCHeader()}
            {renderBCRows(schedD.rows)}
          </Table>
        </div>
      )}

      <div className="font-semibold">{schedD.title}</div>
      <Table className="w-full text-sm">
        <TableHeader>
          <TableRow>
            <TableHead>Metric</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Percent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>Intermediate Total</TableCell>
            <TableCell className="text-right">{formatCurrency(schedD.summary?.intermediate_total || 0)}</TableCell>
            <TableCell className="text-right">—</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>To G&amp;A</TableCell>
            <TableCell className="text-right">{formatCurrency(schedD.summary?.to_ga_amount || 0)}</TableCell>
            <TableCell className="text-right">{((schedD.summary?.ga_pct || 0) * 100).toFixed(2)}%</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>To OH</TableCell>
            <TableCell className="text-right">{formatCurrency(schedD.summary?.to_oh_amount || 0)}</TableCell>
            <TableCell className="text-right">{((schedD.summary?.oh_pct || 0) * 100).toFixed(2)}%</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            <span>ICE Schedules A–E</span>
            <Badge variant="secondary">{filename}</Badge>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="B" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="A">Schedule A</TabsTrigger>
            <TabsTrigger value="B">Schedule B</TabsTrigger>
            <TabsTrigger value="C">Schedule C</TabsTrigger>
            <TabsTrigger value="D">Schedule D</TabsTrigger>
            <TabsTrigger value="E">Schedule E</TabsTrigger>
          </TabsList>

          <TabsContent value="A" className="mt-6">{renderScheduleA()}</TabsContent>

          <TabsContent value="B" className="mt-6">
            <div className="font-semibold mb-2">{schedB.title}</div>
            <Table className="w-full text-sm">
              {renderBCHeader()}
              {renderBCRows(schedB.rows)}
            </Table>
          </TabsContent>

          <TabsContent value="C" className="mt-6">
            <div className="font-semibold mb-2">{schedC.title}</div>
            <Table className="w-full text-sm">
              {renderBCHeader()}
              {renderBCRows(schedC.rows)}
            </Table>
          </TabsContent>

          <TabsContent value="D" className="mt-6">{renderScheduleD()}</TabsContent>

          <TabsContent value="E" className="mt-6">{renderScheduleE()}</TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ICESchedules;
